import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";

interface CountdownTimerProps {
  targetDate: string;
  className?: string;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export function CountdownTimer({ targetDate, className }: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!targetDate) return;

    const calculateTimeLeft = () => {
      const target = new Date(targetDate).getTime();
      const now = new Date().getTime();
      const difference = target - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
        );
        const minutes = Math.floor(
          (difference % (1000 * 60 * 60)) / (1000 * 60),
        );
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeLeft({ days, hours, minutes, seconds });
        setIsExpired(false);
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        setIsExpired(true);
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  if (!targetDate) {
    return null;
  }

  if (isExpired) {
    return (
      <div className={cn("text-center", className)}>
        <div className="text-2xl font-bold text-primary mb-2">
          🎉 Launch Time! 🎉
        </div>
        <p className="text-white/70">
          We're excited to announce that our platform is now live!
        </p>
      </div>
    );
  }

  return (
    <div className={cn("text-center", className)}>
      <div className="grid grid-cols-4 gap-4 mb-4">
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
          <div className="text-3xl font-bold text-primary">{timeLeft.days}</div>
          <div className="text-sm text-white/70 uppercase tracking-wide">
            Days
          </div>
        </div>
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
          <div className="text-3xl font-bold text-primary">
            {timeLeft.hours}
          </div>
          <div className="text-sm text-white/70 uppercase tracking-wide">
            Hours
          </div>
        </div>
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
          <div className="text-3xl font-bold text-primary">
            {timeLeft.minutes}
          </div>
          <div className="text-sm text-white/70 uppercase tracking-wide">
            Minutes
          </div>
        </div>
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
          <div className="text-3xl font-bold text-primary">
            {timeLeft.seconds}
          </div>
          <div className="text-sm text-white/70 uppercase tracking-wide">
            Seconds
          </div>
        </div>
      </div>
      <p className="text-white/60 text-sm">Until launch</p>
    </div>
  );
}
